import { Button } from '@wtfalch/design';
import { useState } from 'react';
import { checkForDesktopUpdate } from '../desktop-version.ts';
import { Board } from './Board.tsx';

/**
 * Shown instead of the whole app when the running desktop shell answered
 * `version()` with something below what this web build requires (agora#37,
 * `desktop-version.ts`). A browser tab never reaches this, and neither does
 * a desktop build old enough to have no `version()` method at all --
 * `isDesktopVersionSupported` fails open for both, so `App.tsx` only
 * renders this once a shell has actually confirmed it is too old.
 *
 * The button itself needs `checkForUpdates()`, a separate, newer bridge
 * method than `version()` -- present on every build that ships this screen,
 * but not guaranteed on whatever future shell might expose `version()`
 * without it, so it renders only when the method is actually there rather
 * than assuming the two always travel together.
 */
export function UpdateRequired() {
  const [state, setState] = useState<'idle' | 'checking' | 'checked' | 'failed'>('idle');
  const canCheck = typeof window.wtfalchDesktop?.checkForUpdates === 'function';

  return (
    <Board title="Update Agora to continue">
      <p>This version of Agora needs a newer desktop app than the one running here.</p>
      {state === 'checked' && (
        <p>
          Update check started. If a newer version is found, it finishes installing the next time
          you quit and reopen Agora.
        </p>
      )}
      {state === 'failed' && <p role="alert">Could not check for an update. Try again shortly.</p>}
      {canCheck ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            kind="primary"
            busy={state === 'checking'}
            onPress={() => {
              setState('checking');
              checkForDesktopUpdate()
                .then(() => setState('checked'))
                .catch(() => setState('failed'));
            }}
          >
            Check for update
          </Button>
        </div>
      ) : (
        <p>Quit and reopen Agora to pick up an update already downloaded in the background.</p>
      )}
    </Board>
  );
}
